import { action } from '@ember/object';
import { debounce, next, run, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import Handsontable from 'handsontable';
import $ from 'jquery';

import type { Service } from 'webapp-types';
import { hexToRgba } from 'webapp/utils/color';
import { equal } from 'webapp/utils/common';
import type {
	IndexSelectionCells,
	IndexSelectionWholeRowOrCol,
	RowCol,
} from 'webapp/utils/sheet-selection';
import SheetSelection from 'webapp/utils/sheet-selection';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import type { ColorSelection } from 'webapp/components/custom-query/setup/sheet/preview';

import type DataSheet from 'webapp/models-custom/data-sheet';

// Handsontable 6 leaves the selection out of its types
type HotInstance = Handsontable & {
	selection: { selectedByRowHeader: Set<number>; selectedByColumnHeader: Set<number> };
};

interface RenderedColorSelection {
	color: string;
	range: IndexSelectionCells;
}

interface Args {
	model: DataSheet;
	isMultipleSheets: boolean;
	colorSelections: ColorSelection[];
	skipCustomBorders: boolean;
	colHeaderFormatter?: (colIdx: number) => string;
	onSheetDataFocused?: () => void;
	onSelectionEnd?: (selection: string) => void;
	onSelectedValueChange?: (selection: string) => void;
}

export default class CustomQueryFormSheetSingleSheetComponent extends Component<Args> {
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;

	@tracked hot: HotInstance | null = null; // Handsontable instance
	@tracked lastValidRange = '';

	node: HTMLElement | null = null;

	get isSelectable(): boolean {
		return !!this.args.onSelectionEnd || !!this.args.onSelectedValueChange;
	}

	getCurrentSelected(): string {
		const hot = this.hot;

		let selected = '';

		if (hot) {
			const selectedRange = hot.getSelectedRange();
			if (selectedRange) {
				const isWholeRow = hot.selection.selectedByRowHeader.size > 0,
					isWholeColumn = hot.selection.selectedByColumnHeader.size > 0,
					selection = SheetSelection.fromIndex(
						selectedRange[0]!,
						isWholeRow,
						isWholeColumn,
					);

				selected = selection.valueA1;
			}
		}

		return selected;
	}

	validateSelectedRange(coords: Handsontable.wot.CellCoords): void {
		if (!this.hot) {
			return;
		}

		// allow only ranges of max one row / column
		const selectedRange = this.hot.getSelectedRange()!;

		if (
			selectedRange[0]!.from.row !== coords.row &&
			selectedRange[0]!.from.col !== coords.col
		) {
			const lastValidRange = this.lastValidRange;

			if (lastValidRange) {
				next(this, function () {
					const hot = this.hot;

					if (!hot) {
						return;
					}

					const selection = SheetSelection.fromA1(lastValidRange);

					if (selection.isWholeRows) {
						const indexes = selection.valueIndex as IndexSelectionWholeRowOrCol;
						hot.selectRows(indexes.from, indexes.to);
					} else if (selection.isWholeCols) {
						const indexes = selection.valueIndex as IndexSelectionWholeRowOrCol;
						hot.selectColumns(indexes.from, indexes.to);
					} else {
						let indexes: IndexSelectionCells;
						if (selection.isRange) {
							indexes = selection.valueIndex as IndexSelectionCells;
						} else {
							const rowCols = selection.valueIndex as RowCol;
							indexes = { from: rowCols, to: rowCols };
						}
						hot.selectCells([
							[indexes.from.row, indexes.from.col, indexes.to.row, indexes.to.col],
						]);
					}
				});
			}
		} else {
			this.lastValidRange = this.getCurrentSelected();
		}
	}

	resizeCt(): void {
		if (!this.node) {
			return;
		}

		// we want to avoid the vertical scrollbars, if possible
		// so we check how much space we need, and how much we have in page

		const ct = $(this.node),
			win = $(window),
			minH = 300,
			wtHider = ct.find('.ht_master .wtHider'),
			ctTop = ct.offset()?.top || 0,
			winH = win.height() || 0,
			winScr = win.scrollTop() || 0;

		let maxH = wtHider.height() || Infinity, // total height of the table with data
			other = 5; // reserve

		if (this.args.model.isTrimmed) {
			other += 24; // trimmed-info
		}

		if (this.args.isMultipleSheets) {
			other += 45; // sheet-switch
		}

		if (wtHider.width()! > ct.width()!) {
			// height needs to take into account horizontal scrollbar
			maxH += 17;
		}

		let ctBottom = 0;
		// sheet wizard needs space on bottom for the controls (other views with sheets stretch to the bottom)
		const sheetWizardNode = this.node.closest('.custom-metric-wizard--sheet');
		if (sheetWizardNode) {
			other += 25; // popup bottom margin (top is already taken into account by ctTop)
			const controlsNode = sheetWizardNode.getElementsByClassName(
				'cmw-controls',
			)[0] as HTMLElement | null;
			if (controlsNode) {
				ctBottom = controlsNode.offsetHeight;
			}
		}

		const newH = Math.min(Math.max(winH + winScr - (ctTop + other + ctBottom), minH), maxH);

		ct.height(newH);

		if (this.hot) {
			this.hot.updateSettings(
				{
					width: ct.width(),
					height: newH,
				},
				false,
			);
			this.mmCustomQueriesService.repositionEditPopup();
		}
	}

	setupSheet(): void {
		if (this.hot) {
			this.hot.destroy();
		}

		const el = document.getElementById('single-sheet');

		if (!el) {
			return;
		}

		const that = this;

		const customRenderer = function (
			this: unknown,
			_instance: Handsontable,
			td: HTMLElement,
			row: number,
			col: number,
			_prop: string | number,
			_value: string,
			cellProperties: Handsontable.GridSettings & { styles?: Record<string, string> },
		) {
			Handsontable.renderers.TextRenderer.apply(this, [
				_instance,
				td,
				row,
				col,
				_prop,
				_value,
				cellProperties,
			]);

			let selectionColor = '',
				selectionBgColor = '';

			(that.args.colorSelections || []).forEach((s) => {
				const selection = SheetSelection.fromA1(s.range),
					cellA1 = SheetSelection.transformCellIndexToA1({ row, col });
				if (selection.isCellIncludedInRange(cellA1)) {
					selectionBgColor = hexToRgba(s.color, 0.1);
					selectionColor = '#292929';
				}
			});

			const styles = Object.assign({}, cellProperties.styles || {});
			if (selectionBgColor || !styles.backgroundColor) {
				styles.backgroundColor = selectionBgColor;
			}
			if (selectionColor || !styles.color) {
				styles.color = selectionColor;
			}

			$(td).css(styles);

			return td;
		};

		const onScroll = function () {
			debounce(that, that.updateRenderedColorSelections, 80);
		};

		const options: Handsontable.GridSettings = {
			readOnly: true,
			selectionMode: 'range',
			// outsideClickDeselects: false,
			colHeaders: (this.args.colHeaderFormatter ||
				true) as Handsontable.GridSettings['colHeaders'],
			rowHeaders: true,
			renderer: customRenderer,
			autoColumnSize: false,
			autoRowSize: false,
			disableVisualSelection: !this.isSelectable,
			// viewportColumnRenderingOffset: 10, // to avoid jumpy row heights when scrolling horizontally

			beforeSetRangeEnd: function (coords: Handsontable.wot.CellCoords) {
				that.validateSelectedRange(coords);
			},
			afterSelectionEnd: function (_r: unknown, _c: unknown, r2: number, c2: number) {
				const hot = that.hot;

				if (!hot) {
					return;
				}

				if (that.args.onSelectionEnd) {
					const selected = that.getCurrentSelected();
					that.args.onSelectionEnd(selected);
				}

				if (that.args.onSelectedValueChange) {
					const cellValue = hot.getDataAtCell(r2, c2);
					that.args.onSelectedValueChange(cellValue || '');
				}
			},
			afterListen: function () {
				// hot has gotten focus, mark that so we know not to unfocus a1 fields
				if (that.args.onSheetDataFocused) {
					that.args.onSheetDataFocused();
				}
			},
			afterScrollHorizontally: onScroll,
			afterScrollVertically: onScroll,
		};

		options.data = this.args.model.dataForRender;
		// HOT 6 types `cell` as an array, but it reads it with objectEach, so the keyed object works
		Object.assign(options, { cell: this.args.model.cellData });
		options.manualColumnResize = this.args.model.colWidths;
		options.manualRowResize = this.args.model.rowHeights;
		options.mergeCells = this.args.model.mergeCells;

		const hot = new Handsontable(el, options) as HotInstance;

		this.hot = hot;

		this.resizeCt();

		this.updateRenderedColorSelections();
		this.updateCustomBorders();
	}

	//----- marked border selection logic start
	renderedColorSelections: RenderedColorSelection[] = []; // performance issues when doing borders on ALL column etc

	updateCustomBorders(): void {
		if (!this.hot) {
			return;
		}

		if (this.args.skipCustomBorders) {
			return;
		}

		const customBorders: object[] = [];

		this.renderedColorSelections.forEach((item) => {
			const borderSettings = {
				width: 1,
				color: item.color,
			};
			customBorders.push({
				range: item.range,
				top: borderSettings,
				left: borderSettings,
				bottom: borderSettings,
				right: borderSettings,
			});
		});

		this.hot.updateSettings(
			{
				customBorders: customBorders,
			},
			false,
		);
	}

	a1ToCellRange(hot: Handsontable, a1: string): IndexSelectionCells {
		const selection = SheetSelection.fromA1(a1),
			maxColumn = hot.countCols() - 1,
			maxRow = hot.countRows() - 1;

		let from: RowCol | null = null,
			to: RowCol | null = null;

		// if whole row / column selected, only include cells
		if (selection.isWholeRows) {
			const valueIndex = selection.valueIndex as IndexSelectionWholeRowOrCol;
			from = { row: valueIndex.from, col: 0 };
			to = { row: valueIndex.to, col: maxColumn };
		} else if (selection.isWholeCols) {
			const valueIndex = selection.valueIndex as IndexSelectionWholeRowOrCol;
			from = { col: valueIndex.from, row: 0 };
			to = { col: valueIndex.to, row: maxRow };
		} else if (!selection.isRange) {
			const valueIndex = selection.valueIndex as RowCol;
			from = valueIndex;
			to = valueIndex;
		}

		if (to && from) {
			return { to, from };
		} else {
			return selection.valueIndex as IndexSelectionCells;
		}
	}

	@action
	updateRenderedColorSelections(): void {
		const hot = this.hot;

		if (!hot) {
			return;
		}

		const renderedColorSelections: RenderedColorSelection[] = [];

		const bufferCells = 2, // how many more we add to the viewpoint
			fromRow = Math.max(0, hot.getPlugin('autoRowSize').getFirstVisibleRow() - bufferCells),
			fromCol = Math.max(
				0,
				hot.getPlugin('autoColumnSize').getFirstVisibleColumn() - bufferCells,
			),
			toRow = fromRow + hot.countVisibleRows() + 2 * bufferCells,
			toCol = fromCol + hot.countVisibleCols() + 2 * bufferCells;

		(this.args.colorSelections || []).forEach((item) => {
			const range = this.a1ToCellRange(hot, item.range);

			if (
				range.to.row >= fromRow &&
				range.from.row <= toRow &&
				range.to.col >= fromCol &&
				range.from.col <= toCol
			) {
				range.from.row = Math.max(range.from.row, fromRow);
				range.from.col = Math.max(range.from.col, fromCol);
				range.to.row = Math.min(range.to.row, toRow);
				range.to.col = Math.min(range.to.col, toCol);

				renderedColorSelections.push({
					range: range,
					color: item.color,
				});
			}
		});

		if (!equal(this.renderedColorSelections, renderedColorSelections)) {
			this.renderedColorSelections = renderedColorSelections;
			this.updateCustomBorders();
		}
	}
	// ---- marked border selection logic end

	@action
	modelChanged(): void {
		scheduleOnce('afterRender', this, this.setupSheet);
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;

		this.setupSheet();

		$(window).on('resize.single-sheet', () => {
			run(this, this.resizeCt);
		});
	}

	@action
	teardownView(): void {
		$(window).off('resize.single-sheet');
	}
	<template>
		<div
			id="single-sheet-ct"
			{{didInsert this.setupView}}
			{{didUpdate this.modelChanged @model}}
			{{didUpdate this.updateRenderedColorSelections @colorSelections}}
			{{willDestroy this.teardownView}}
		>
			<div id="sheet-fake-header"></div>
			<div id="single-sheet"></div>
		</div>
	</template>
}
